import { useCallback, useMemo, forwardRef } from 'react';

import { TFunction, useTranslation } from 'react-i18next';

import { useAutomationsSelector } from 'pages/AutomationEngine/store/react';
import { getAutomationIntializationVariables } from 'pages/AutomationEngine/store/selectors';
import { GroupedVariableSelectWithAdd } from 'pages/AutomationEngine/dialogs/VariableSelect/GroupedVariableSelectWithAdd';

import Cols from '__components/Layout/Cols';
import {
  SourceVariableType,
  StaticVariableType,
  AutomationVariableType,
  VariableValidation,
} from 'pages/AutomationEngine/dialogs/VariableWizard/types';
import { AutomationVariableValidationKeys } from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/ValidationKeys';
import {
  fieldValidate,
  FieldValidation,
} from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/utilties';
import { useErrors } from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/useErrors';
import { AUTOMATION_VARIABLE_DATA_TYPES } from 'pages/AutomationEngine/constants';

import { useVariableContext } from 'pages/AutomationEngine/dialogs/VariableWizard/variable-types/hooks/useVariableContext';

import { DeletedOption, VariableOption } from 'pages/AutomationEngine/types';
import { addDeleted } from '__pages/AutomationEngine/dialogs/common';

const defaultState: StaticVariableType = {
  value: '',
  valueHtml: '',
  isValid: true,
  valueCategory: undefined,
  variableType: AUTOMATION_VARIABLE_DATA_TYPES.string,
  id: Date.now().toString(),
};

const emptyArray: any[] = [];

const useComponentState = (values: SourceVariableType, t: TFunction) => {
  const {
    updateVariableSource,
    variable,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    setErrors: _setErrors,
    errors: _errors,
  } = useVariableContext(values?.forInitialize!);

  const variables: VariableOption[] =
    useAutomationsSelector(getAutomationIntializationVariables) || emptyArray;

  const [variableOptions, allowedDataTypes] = useMemo(() => {
    return [
      variables.filter(
        (v) => v.meta && checkVariableVariableCompatibility(v.meta, variable)
      ),
      getVariableVariableCompatibility(variable),
    ];
  }, [
    variables,
    checkVariableVariableCompatibility,
    getVariableVariableCompatibility,
    variable,
  ]);

  const { automationVariable = defaultState, id } = values;

  const { setError, errors, validate, clearError } = useErrors(
    id,
    _errors,
    _setErrors
  );

  const { value } = automationVariable!;
  const error = errors[id];

  const updateAutomationVariable = useCallback(
    (automationVariable: AutomationVariableType) => {
      updateVariableSource({
        ...values,
        automationVariable,
      });
    },
    [updateVariableSource, values]
  );

  const setValue = useCallback(
    (value: AutomationVariableValidationKeys | string | undefined) => {
      updateAutomationVariable({ ...automationVariable, value });
      clearError(id);
    },
    [clearError, automationVariable, updateAutomationVariable, id]
  );

  const valueWithDeleted = useMemo(
    () => addDeleted(value as DeletedOption, t),
    [value, t]
  );

  return {
    value: valueWithDeleted,
    setValue,

    error,
    setError,
    validate,

    variableOptions,
    allowedDataTypes,
  };
};

export const Component = forwardRef((values: SourceVariableType, ref) => {
  const { t } = useTranslation();

  const {
    value,
    setValue,

    error,
    validate,

    variableOptions,
    allowedDataTypes,
  } = useComponentState(values, t);

  return (
    <Cols columns={3} gutter="20px" ref={ref}>
      <GroupedVariableSelectWithAdd
        label={t('Variable')}
        placeholder={t('Choose Variable')}
        error={Boolean(error?.value)}
        validate={validate}
        value={value}
        options={variableOptions}
        allowedDataTypes={allowedDataTypes}
        onChange={setValue as unknown as (value: VariableOption) => void}
        menuInline
        data-qa="automation-variable-value"
      />
    </Cols>
  );
});

const getFieldsToValidate = (t: TFunction): FieldValidation => ({
  value: [
    {
      type: 'deleted-variable',
      fieldType: undefined,
      errorMessage: t('Please select a valid variable'),
      deletedNameLookup: { fallback: t('Variable') },
    },
  ],
});

export const validate = ({ values, checkRequired, t }: VariableValidation) => {
  const { automationVariable, sourceSubtype } = values;
  return fieldValidate(
    automationVariable,
    getFieldsToValidate(t),
    sourceSubtype,
    checkRequired,
    t
  );
};
